import { resolveFormTenantId } from "../../utils/tenantContext";
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import PropTypes from 'prop-types';
import { governmentFormSchema } from './application.validation';
import CloseButton from "../../components/Application/CloseButton";
import { request } from '../../utils/api';
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import EvaluationFormHeader from '../EvaluationNew/components/EvaluationFormHeader';
import { getLocalSystemDate, parseIsoDateToMDYUS } from '../../utils/dateUtils';
import { buildPayloadWithIsoDates } from '../../utils/applicationFormPayload';

const DirectDepositSetupGuide = ({ positionName, onClose }) => {
    const [guideId, setGuideId] = useState(null);
    const [loading, setLoading] = useState(false);

    const methods = useForm({
        resolver: zodResolver(governmentFormSchema),
        defaultValues: {
            tenantId: undefined,
            userId: undefined,
            applicantSignature: "",
            applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
            reviewedBySignature: "",
            reviewedBySignedAt: "",
            reviewedBySignedBy: ""
        },
        mode: "onChange"
    });

    const { reset } = methods;

    const [currentStatus, setCurrentStatus] = useState("");

    const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
        const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
        try {
            const payload = buildPayloadWithIsoDates(
                data,
                additionalData,
                ['applicantSignedAt', 'reviewedBySignedAt'],
                { flag: actualFlag }
            );
            payload.positionAppliedFor = positionName;

            // Handle To Be Corrected comments
            const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
            if (finalComments) {
                payload.comments = finalComments;
            }

            const method = guideId ? 'PUT' : 'POST';
            const url = guideId ? `/direct-deposit-guides/${guideId}` : '/direct-deposit-guides';

            const response = await request(url, {
                method,
                body: JSON.stringify(payload)
            });

            if (response.status === 1) {
                toast.success(response.message || "Form processed successfully!");
                closeForm();
            } else {
                toast.error(response.message || "Failed to process form");
            }
        } catch (error) {
            console.error("Error submitting Direct Deposit Guide:", error);
            toast.error("Failed to submit form");
        }
    };

    const {
        targetUserId,
        user: currentUserData,
        isAdmin,
        closeForm,
        actualFromId
    } = useApplicationFormLogic({
        methods,
        applicantSignatureField: "applicantSignature",
        applicantSignatureDateField: "applicantSignedAt",
        adminSignatureField: "reviewedBySignature",
        adminSignatureDateField: "reviewedBySignedAt",
        onClose,
        onSubmit,
        currentStatus
    });

    useEffect(() => {
        const fetchData = async () => {
            // Must wait for currentUserData
            if (!currentUserData) return;
            if (isAdmin && !targetUserId && !guideId) return;

            setLoading(true);
            try {
                if (!actualFromId && !targetUserId && !guideId) {
                    if (currentUserData && !isAdmin) {
                        reset({
                            tenantId: resolveFormTenantId(currentUserData?.tenantId),
                            userId: currentUserData.id,
                            positionAppliedFor: positionName || "",
                            applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
                        });
                    }
                    setLoading(false);
                    return;
                }

                let response;
                if (isAdmin) {
                    if (actualFromId) {
                        response = await request(`/direct-deposit-guides/${actualFromId}?userId=${targetUserId}`);
                    } else {
                        response = await request(`/direct-deposit-guides?userId=${targetUserId}`);
                    }
                } else {
                    response = await request(`/direct-deposit-guides/${actualFromId || targetUserId}`);
                }

                if (response.status === 1 && response.result) {
                    const data = response.result.guideForm || response.result;
                    setGuideId(data.id);
                    setCurrentStatus(data.status || "");
                    reset({
                        ...data,
                        applicantSignature: data.applicantSignature || "",
                        applicantSignedAt: (data.applicantSignedAt || "").split('T')[0],
                        reviewedBySignature: isAdmin ? (data.reviewedBySignature || data.adminSignature || "") : "",
                        reviewedBySignedAt: isAdmin ? (data.reviewedBySignedAt || data.adminSignatureDate || "").split('T')[0] : "",
                        reviewedBySignedBy: isAdmin ? (data.reviewedBySignedBy || "") : ""
                    });
                }
            } catch (error) {
                console.error("Error fetching Direct Deposit Guide data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [targetUserId, reset, currentUserData, isAdmin, guideId, actualFromId, positionName]);



    if (loading) return <div className="text-center py-10">Loading Direct Deposit Guide...</div>;

    return (
        <>
            <CloseButton onClose={closeForm} />
            <div className="bg-white p-4 max-w-6xl mx-auto shadow-lg rounded-xl my-4">
                <EvaluationFormHeader
                    title="Direct Deposit"
                />

                <div className="mb-4 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-100">
                    <iframe
                        src="/DirectDepositSetupGuide.pdf#toolbar=1"
                        className="w-full"
                        style={{ minHeight: 'calc(100vh - 250px)', height: 'calc(100vh - 250px)' }}
                        title="Direct Deposit Set-up Guide"
                        allow="fullscreen"
                    />
                </div>

                {/* <FormProvider {...methods}>
                    <form>
                        <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
                        <ApplicationFormFooter
                            page={1}
                            totalPages={1}
                            methods={methods}
                            onSave={handleSave}
                            onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                            onStatusUpdate={handleStatusAction}
                            isSignatureModalOpen={isSignatureModalOpen}
                            openSignatureModal={openSignatureModal}
                            closeSignatureModal={closeSignatureModal}
                            handleSignatureApply={handleSignatureApply}
                            signerName={signerName}
                            applicantSignature={watch('applicantSignature')}
                            applicantSignedAt={watch('applicantSignedAt')}
                            reviewedBySignedBy={watch('reviewedBySignedBy') || signerName}
                            reviewedBySignature={watch('reviewedBySignature')}
                            reviewedBySignedAt={watch('reviewedBySignedAt')}
                            showCertification={false}
                            isReadOnly={isReadOnly}
                            onClose={closeForm}
                            currentStatus={currentStatus}
                        />
                    </form>
                </FormProvider> */}
            </div>
        </>
    );
};

DirectDepositSetupGuide.propTypes = {
    positionName: PropTypes.string,
    onClose: PropTypes.func
};

export default DirectDepositSetupGuide;
